import { createContext, useCallback, useContext, useState, type ReactNode } from "react"

type ConfirmOptions = {
  title: string
  description: string
  confirmLabel?: string
  destructive?: boolean
}

type ConfirmContextValue = (options: ConfirmOptions) => Promise<boolean>
const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<{
    options: ConfirmOptions
    resolve: (value: boolean) => void
  } | null>(null)
  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ options, resolve })),
    []
  )
  const finish = (value: boolean) => {
    pending?.resolve(value)
    setPending(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-muted-foreground/40 p-4" role="presentation">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-lg" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
            <h2 id="confirm-title" className="font-semibold">{pending.options.title}</h2>
            <p className="text-muted-foreground mt-2 text-sm">{pending.options.description}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button className="h-9 rounded-lg border border-input px-4 text-sm font-medium hover:bg-muted" onClick={() => finish(false)}>Cancel</button>
              <button className={`h-9 rounded-lg px-4 text-sm font-medium ${pending.options.destructive ? "bg-destructive text-background" : "bg-primary text-primary-foreground"}`} onClick={() => finish(true)}>{pending.options.confirmLabel ?? "Confirm"}</button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const value = useContext(ConfirmContext)
  if (!value) throw new Error("useConfirm must be used within ConfirmProvider")
  return value
}

type Toast = { id: number; message: string; destructive?: boolean }
const ToastContext = createContext<((message: string, destructive?: boolean) => void) | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const toast = useCallback((message: string, destructive = false) => {
    const id = Date.now() + Math.random()
    setToasts((current) => [...current, { id, message, destructive }])
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 5000)
  }, [])
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[90] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2" aria-live="polite">
        {toasts.map((item) => <div key={item.id} className={`pointer-events-auto rounded-lg border bg-card px-4 py-3 text-sm shadow-md ${item.destructive ? "border-destructive" : "border-border"}`}>{item.message}</div>)}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const value = useContext(ToastContext)
  if (!value) throw new Error("useToast must be used within ToastProvider")
  return value
}
